import EmblaCarousel from '@/components/EmblaCarousel';
import { ContainerElement } from '@/containerElement';
import { Heading3Element } from '@/headingElement';
import { ParagraphElement } from '@/paragraphElement';
import { SerializeTextLeaf } from '@/textLeaf';
import type { RSSFeedDataYouTube, RSSFeedDataYouTubeEntry } from '@/types';
import SimpleModal from '@/ui/SimpleModal';
import { getBackgroundColorStyle } from '@/utils/background';
import { cn } from '@/utils/cn';
import { useMobileCarouselSettings } from '@/utils/useMobileCarouselSettings';
import { type CSSProperties, type PropsWithChildren, useState } from 'react';
import * as styles from './RSSFeed.css';
import type { RSSFeedElementProps } from './RSSFeed.types';
import { YoutubeLogoSVG } from './YouTubeLogo';
import { formatDate } from './util';

const YOUTUBE_RSS_FEED_DEFAULTS = {
  imageBorderRadius: '8px',
  titleFontSize: 24,
  titleFontFamily: 'Inter',
  subtitleFontSize: 14,
  subtitleFontFamily: 'Inter',
  titleTextColor: 'inherit',
  subtitleTextColor: 'inherit',
  titleFontWeight: 700,
  subtitleFontWeight: 400,

  borderRadius: '8px',
  borderWidth: '1px',
  borderColor: 'inherit',
  borderStyle: 'none',
  background: 'transparent',
};

const COLUMNS_MAP: Record<string, string> = {
  '1': 'grid-cols-1',
  '2': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
  '3': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
  '4': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
  '5': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
  '6': 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-6',
};

const OPTIONS: any = {
  align: 'start',
  containScroll: 'trimSnaps',
};

export const YouTubeRssFeedCard = (
  props: Omit<RSSFeedElementProps, 'children'> & {
    entry: RSSFeedDataYouTubeEntry;
    onOpenModal: (videoId: string) => void;
  },
) => {
  const { entry, element, onClick, isPreviewing, onOpenModal } = props;
  const {
    itemPadding,
    itemGap,
    imageBorderRadius,
    imageAspectRatio,
    imageFit,
    titleFontSize,
    titleFontFamily,
    titleTextColor,
    titleFontWeight,
    titleAlignment,
    titleLineClamp,
    subtitleFontSize,
    subtitleFontFamily,
    subtitleTextColor,
    subtitleFontWeight,
    subtitleAlignment,
    borderRadius,
    borderWidth,
    borderColor,
    borderStyle,
    backgroundColor,
    textGap,
    showPlayButton,
    playAction,
    hideDate,
  } = element.attrs;

  const youtubeThumbnailDefaultSize = entry['media:group']?.['media:thumbnail']?.['@url']; // hqdefault.jpg
  const youtubeThumbnailBaseURL = youtubeThumbnailDefaultSize?.split('/').slice(0, -1).join('/') || '';
  const youtubeThumbnailMaxSize = `${youtubeThumbnailBaseURL}/maxresdefault.jpg`;

  return (
    <a
      href={entry.link?.['@href']}
      key={entry.id}
      className={cn(
        'flex flex-col items-start cursor-pointer embla__slide relative',
        styles.youtubeCard,
        isPreviewing && 'pointer-events-none',
      )}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      style={
        {
          padding: itemPadding,
          gap: itemGap || '8px',

          '--card-border-radius': borderRadius || YOUTUBE_RSS_FEED_DEFAULTS.borderRadius,
          '--card-border-width': borderWidth || YOUTUBE_RSS_FEED_DEFAULTS.borderWidth,
          '--card-border-color': borderColor || YOUTUBE_RSS_FEED_DEFAULTS.borderColor,
          '--card-border-style': borderStyle || YOUTUBE_RSS_FEED_DEFAULTS.borderStyle,
          ...getBackgroundColorStyle(backgroundColor),
        } as CSSProperties
      }
    >
      <ContainerElement
        element={{
          type: 'container',
          attrs: {
            id: 'image-container',
            padding: '0px',
            position: 'relative',
            backgroundColor: 'transparent',
            height: 'auto',
            borderRadius: imageBorderRadius || YOUTUBE_RSS_FEED_DEFAULTS.imageBorderRadius,
            overflow: 'hidden',
          },
          content: [],
        }}
        onClick={(e) => {
          if (isPreviewing) {
            return;
          }
          if (playAction === 'modal') {
            e.preventDefault();
            e.stopPropagation();
            onOpenModal(entry['yt:videoId']);
          }
          // If type link, it's already handled by the anchor tag
        }}
      >
        <img
          src={youtubeThumbnailDefaultSize}
          srcSet={`
              ${youtubeThumbnailDefaultSize} 1x,
              ${youtubeThumbnailMaxSize} 2x`}
          alt="YouTube Thumbnail"
          className="w-full h-auto"
          style={{
            aspectRatio: imageAspectRatio || '16/9',
            objectFit: imageFit || 'cover',
          }}
        />
        {showPlayButton && (
          <ContainerElement
            element={{
              type: 'container',
              attrs: {
                id: 'play-button-container',
                position: 'absolute',
                top: '0',
                left: '0',
                padding: '0px',
                backgroundColor: 'transparent',
                width_max: '100%',
                height: '100%',
                zIndex: 10,
              },
              content: [],
            }}
          >
            <YoutubeLogoSVG width={64} height={64} />
          </ContainerElement>
        )}
      </ContainerElement>
      <ContainerElement
        element={{
          type: 'container',
          attrs: {
            id: 'container',
            padding: '0px',
            backgroundColor: 'transparent',
            gap: textGap || '8px',
          },
          content: [],
        }}
      >
        <Heading3Element
          element={{
            type: 'heading',
            attrs: {
              id: 'heading3',
              anchorId: 'heading3',
              padding: '0px',
              anchorTitle: 'heading3',
              anchorIdSync: true,
              anchorEnabled: true,
              anchorTitleSync: true,
              customAttributes: {},
              textAlign: titleAlignment || 'left',
              level: 3,
            },
            content: [
              {
                text: entry.title,
              },
            ],
          }}
          className={titleLineClamp ? `${titleLineClamp} text-ellipsis` : ''}
        >
          <SerializeTextLeaf
            leaf={{
              text: entry.title,
              marks: [
                {
                  type: 'textStyle',
                  attrs: {
                    fontSize: titleFontSize || YOUTUBE_RSS_FEED_DEFAULTS.titleFontSize,
                    fontFamily: titleFontFamily || YOUTUBE_RSS_FEED_DEFAULTS.titleFontFamily,
                    color: titleTextColor || YOUTUBE_RSS_FEED_DEFAULTS.titleTextColor,
                    fontWeight: titleFontWeight || YOUTUBE_RSS_FEED_DEFAULTS.titleFontWeight,
                  },
                },
              ],
            }}
          />
        </Heading3Element>
        {!hideDate && (
          <ParagraphElement
            element={{
              type: 'paragraph',
              attrs: {
                textAlign: subtitleAlignment || 'left',
                padding: '0px',
              },
            }}
          >
            <SerializeTextLeaf
              leaf={{
                text: formatDate(entry.published),
                marks: [
                  {
                    type: 'textStyle',
                    attrs: {
                      fontSize: subtitleFontSize || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontSize,
                      fontFamily: subtitleFontFamily || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontFamily,
                      color: subtitleTextColor || YOUTUBE_RSS_FEED_DEFAULTS.subtitleTextColor,
                      fontWeight: subtitleFontWeight || YOUTUBE_RSS_FEED_DEFAULTS.subtitleFontWeight,
                    },
                  },
                ],
              }}
            />
          </ParagraphElement>
        )}
      </ContainerElement>
    </a>
  );
};

export const YouTubeRssFeed = (props: PropsWithChildren<RSSFeedElementProps>) => {
  const { element, attributes, onClick, children, className, isPreviewing } = props;
  const {
    data: rawData,
    gap,
    padding,
    isViewportMobile,
    isScrollHorizontally,
    isMobileScrollHorizontally,
    rssSelectionType,
    selectedRSSItems,
  } = element.attrs;

  const {
    columns: effectiveColumns,
    shouldScrollHorizontally: displayedShouldScrollHorizontally,
    sliderControlsDisplayType: displayedSliderControlsDisplayType,
    sliderControlsBackgroundColor: displayedSliderControlsBackgroundColor,
    sliderControlsTextColor: displayedSliderControlsTextColor,
    sliderControlsBorderColor: displayedSliderControlsBorderColor,
  } = useMobileCarouselSettings({
    attributes: {
      ...element.attrs,
      shouldScrollHorizontally: isScrollHorizontally,
      mobileShouldScrollHorizontally: isMobileScrollHorizontally,
    },
    isViewportMobile: isViewportMobile || false,
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalVideoId, setModalVideoId] = useState('');
  const showCount = element.attrs.showCount || 5;

  const data = rawData as RSSFeedDataYouTube;

  // If using free selection with selected items, skip data validation
  const hasSelectedItems = rssSelectionType === 'free_selection' && selectedRSSItems && selectedRSSItems.length > 0;

  if (!hasSelectedItems && !data?.feed?.entry) {
    return null;
  }

  const desktopSlideSize =
    displayedShouldScrollHorizontally && !!effectiveColumns ? `${100 / Number.parseInt(effectiveColumns)}%` : '';

  // Use selected items for free selection, otherwise use latest from feed
  const shownData = hasSelectedItems
    ? (selectedRSSItems as RSSFeedDataYouTubeEntry[])
    : Array.isArray(data.feed.entry)
      ? data.feed.entry.slice(0, showCount)
      : [data.feed.entry];

  return (
    <>
      <div
        className={cn(
          'relative w-full h-fit',
          className,
          styles.rssFeedGrid,
          displayedShouldScrollHorizontally ? 'flex' : 'grid',
          !displayedShouldScrollHorizontally && COLUMNS_MAP[effectiveColumns || '3'],
        )}
        style={
          {
            '--gap': gap || '16px',
            padding: padding,

            // Desktop Slider
            '--desktop-slide-size': desktopSlideSize,
            '--post-slider-control-background-color': displayedSliderControlsBackgroundColor,
            '--post-slider-control-text-color': displayedSliderControlsTextColor,
            '--post-slider-control-border-color': displayedSliderControlsBorderColor,

            // Mobile Slider
            '--mobile-slider-control-background-color':
              element.attrs.mobileSliderControlsBackgroundColor || displayedSliderControlsBackgroundColor,
            '--mobile-slider-control-text-color':
              element.attrs.mobileSliderControlsTextColor || displayedSliderControlsTextColor,
            '--mobile-slider-control-border-color':
              element.attrs.mobileSliderControlsBorderColor || displayedSliderControlsBorderColor,
          } as CSSProperties
        }
        {...attributes}
      >
        <EmblaCarousel
          options={OPTIONS}
          isCarousel={!!displayedShouldScrollHorizontally}
          isLive
          controlsDisplayType={displayedSliderControlsDisplayType}
        >
          {shownData.map((entry) => (
            <YouTubeRssFeedCard
              key={entry.id}
              entry={entry}
              element={element}
              attributes={attributes}
              isPreviewing={isPreviewing}
              onClick={onClick}
              onOpenModal={(videoId) => {
                setModalVideoId(videoId);
                setIsModalOpen(true);
              }}
            />
          ))}
        </EmblaCarousel>
        {children}
      </div>

      <SimpleModal
        key={`${modalVideoId}-modal`}
        isOpen={isModalOpen}
        onClose={(value) => {
          setIsModalOpen(value);
          setModalVideoId('');
        }}
        overlayClassName="bg-black/50"
      >
        {isModalOpen && (
          <iframe
            width="560"
            height="315"
            src={`https://www.youtube.com/embed/${modalVideoId}`}
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        )}
      </SimpleModal>
    </>
  );
};
